/**
 * What push draws: the one-time rationale dialog and the foreground toast.
 *
 * ── IN THE APP TREE ─────────────────────────────────────────────────────────
 * Until T46 this mounted a React root of its own on `<body>`, with a second
 * `ToastProvider` in it, because none was mounted in the app tree. There is
 * now one, above the session gate (`app/shell.tsx`), so `PushRouting`
 * (`index.tsx`) renders this component there instead and its toast shares
 * that single viewport. It still needs no router, no Query client and no
 * session: `usePushRouting` hands it a model to show and the actions to call.
 * The strings are Paraglide functions, which read the app's locale from the
 * same global runtime.
 *
 * Rendered on the device only: on the web push is a no-op and the hook
 * returns `null`, so nothing here is drawn.
 */
import {
  "push.rationale_checkin" as rationaleCheckin,
  "push.rationale_enable" as rationaleEnable,
  "push.rationale_later" as rationaleLater,
  "push.rationale_lede" as rationaleLede,
  "push.rationale_reminder" as rationaleReminder,
  "push.rationale_session" as rationaleSession,
  "push.rationale_title" as rationaleTitle,
  "push.rationale_trainer" as rationaleTrainer,
  "push.toast_open" as toastOpen,
} from "@repo/i18n";
import {
  AlarmClock,
  type LucideIcon,
  MessageCircle,
  Sparkles,
  Sun,
} from "lucide-react";
import { useCallback, useEffect, useSyncExternalStore } from "react";
import { Button, Icon } from "../../ui/controls";
import { Dialog, useToast } from "../../ui/overlay";

export interface PushToast {
  /** The push's `data`, routed by "Abrir" exactly like a tap. */
  readonly data: unknown;
  readonly message: string;
}

export interface PushLayerModel {
  readonly rationaleOpen: boolean;
  /** A new object per push, so the same text twice is two toasts. */
  readonly toast: PushToast | null;
}

export interface PushLayerActions {
  /** "Agora não", the close button, Escape or a tap outside. */
  decline(): void;
  /** "Ativar": the only path to the OS permission prompt. */
  enable(): void;
  /** The toast's "Abrir". */
  open(data: unknown): void;
}

export interface PushLayerStore {
  get(): PushLayerModel;
  set(patch: Partial<PushLayerModel>): void;
  subscribe(listener: () => void): () => void;
}

export function createPushLayerStore(): PushLayerStore {
  let model: PushLayerModel = { rationaleOpen: false, toast: null };
  const listeners = new Set<() => void>();
  return {
    get: () => model,
    set(patch) {
      model = { ...model, ...patch };
      for (const listener of listeners) {
        listener();
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export interface PushLayerProps {
  readonly actions: PushLayerActions;
  readonly store: PushLayerStore;
}

function Kind(props: { icon: LucideIcon; text: string }) {
  return (
    <li className="flex items-start gap-3">
      <span className="grid size-9 shrink-0 place-items-center rounded-full bg-surface text-ink">
        <Icon icon={props.icon} />
      </span>
      <span className="pt-1.5 text-base text-ink">{props.text}</span>
    </li>
  );
}

export function PushLayer({ actions, store }: PushLayerProps) {
  const model = useSyncExternalStore(store.subscribe, store.get, store.get);
  const { notify } = useToast();
  const { toast } = model;

  useEffect(() => {
    if (toast === null) {
      return;
    }
    notify({
      action: { label: toastOpen(), onAction: () => actions.open(toast.data) },
      message: toast.message,
      // A push is a message, not a confirmation: no success check (T48).
      tone: "info",
    });
  }, [toast, notify, actions]);

  const openChange = useCallback(
    (open: boolean) => {
      if (!open) {
        actions.decline();
      }
    },
    [actions]
  );

  return (
    <Dialog
      description={rationaleLede()}
      footer={
        <div className="flex gap-3">
          <Button
            className="flex-1"
            onClick={actions.decline}
            variant="secondary"
          >
            {rationaleLater()}
          </Button>
          <Button className="flex-1" onClick={actions.enable}>
            {rationaleEnable()}
          </Button>
        </div>
      }
      onOpenChange={openChange}
      open={model.rationaleOpen}
      title={rationaleTitle()}
    >
      <ul className="flex flex-col gap-3">
        <Kind icon={AlarmClock} text={rationaleReminder()} />
        <Kind icon={Sparkles} text={rationaleSession()} />
        <Kind icon={Sun} text={rationaleCheckin()} />
        <Kind icon={MessageCircle} text={rationaleTrainer()} />
      </ul>
    </Dialog>
  );
}
